/*
  Kiwi Ears · support help hub (Rene 2026-08-22).

  Three behaviours, each aimed at findability rather than decoration:

  1. SEARCH across every answer on the page. Typing filters entries in place and
     hides a group that has nothing left, so the page becomes a shortlist rather
     than a wall you scroll. The count says how many answers match, because a
     silent filter leaves you unsure whether it worked.

  2. ACCORDIONS, one open at a time, following the pattern the PDP FAQ already
     uses (aria-expanded on the button, GSAP height on the panel, chevron flip)
     so the two surfaces behave identically. Shorter than the PDP's 0.5s: a
     disclosure is a small, frequently repeated action, and 0.28s reads as
     immediate where half a second reads as syrup.

  3. THE JUMP RAIL tracks the section you are reading, via ScrollTrigger so it
     stays in step with Lenis. That is the one genuinely useful piece of motion
     here: on a long help page you need to know where you are.

  A search hit auto-opens its answer, because filtering to one result and then
  making the reader click it is a pointless extra step.

  Everything degrades: no GSAP, no animation but the accordions still open;
  reduced motion, no height tween and no scroll tracking.
*/
(function () {
  var root = document.getElementById('sp-help');
  if (!root) return;

  var search = root.querySelector('#sp-q');
  var clearBtn = root.querySelector('#sp-clear');
  var count = root.querySelector('#sp-count');
  var none = root.querySelector('#sp-none');
  var items = [].slice.call(root.querySelectorAll('.sp-item'));
  var groups = [].slice.call(root.querySelectorAll('.sp-group'));
  var navLinks = [].slice.call(root.querySelectorAll('.sp-navlink'));

  var still = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var g = window.gsap;

  /* ---------- accordions ---------- */
  function setOpen(item, open) {
    var btn = item.querySelector('button');
    var ans = item.querySelector('.sp-ans');
    var chev = item.querySelector('.sp-chev');
    btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (chev) chev.textContent = open ? '−' : '+';   /* minus / plus */

    /* A closed panel is h-0 + overflow-hidden: invisible, but its "More detail"
       link stayed in the tab order, so keyboard users collected a focus stop for
       every answer on the page. `inert` takes the whole subtree out of both the
       tab order and the a11y tree without touching layout, so GSAP can still
       measure it. Lifted before opening, applied after closing. */
    if (open) ans.removeAttribute('inert');

    if (g && !still) {
      g.to(ans, {
        height: open ? 'auto' : 0, duration: 0.28, ease: 'power2.inOut', overwrite: 'auto',
        onComplete: function () { if (!open) ans.setAttribute('inert', ''); }
      });
    } else {
      ans.style.height = open ? 'auto' : '0';
      if (!open) ans.setAttribute('inert', '');
    }
  }

  items.forEach(function (item) {
    item.querySelector('button').addEventListener('click', function () {
      var open = item.querySelector('button').getAttribute('aria-expanded') === 'true';
      items.forEach(function (other) { if (other !== item) setOpen(other, false); });
      setOpen(item, !open);
    });
  });

  /* ---------- search ---------- */
  /* Nobody types the same number as the copywriter. "returns" has to find
     "return", "cables" has to find "cable". One retry without the trailing s
     when the exact term finds nothing — enough for the plural mismatch that
     actually happens, without pretending to be a stemmer. */
  function matches(item, term) {
    var hay = item.getAttribute('data-q') || '';
    if (hay.indexOf(term) > -1) return true;
    if (term.length > 3 && term.charAt(term.length - 1) === 's') {
      return hay.indexOf(term.slice(0, -1)) > -1;
    }
    return false;
  }

  function apply() {
    var term = (search.value || '').toLowerCase().trim();
    var hits = 0;

    items.forEach(function (item) {
      var hit = term === '' || matches(item, term);
      item.classList.toggle('hidden', !hit);
      if (hit) hits++;
      /* a single hit is almost certainly the answer, so open it */
      if (term !== '' && hit) setOpen(item, true);
      if (term === '') setOpen(item, false);
    });

    groups.forEach(function (group) {
      var live = [].slice.call(group.querySelectorAll('.sp-item')).filter(function (i) {
        return !i.classList.contains('hidden');
      }).length;
      group.classList.toggle('hidden', live === 0);
      var link = root.querySelector('.sp-navlink[data-nav-for="' + group.getAttribute('data-group') + '"]');
      if (link) {
        var c = link.querySelector('.sp-navcount');
        if (c) c.textContent = term === '' ? '' : live;
        link.classList.toggle('is-empty', live === 0);
      }
    });

    if (count) {
      var n = fmt(term === '' ? items.length : hits);
      count.textContent = term === ''
        ? n + ' answers'
        : n + (hits === 1 ? ' answer' : ' answers') + ' for “' + term + '”';
    }
    if (none) {
      none.classList.toggle('hidden', hits > 0);
      none.classList.toggle('flex', hits === 0);
    }
    if (clearBtn) clearBtn.classList.toggle('is-on', search.value !== '');
  }

  /* Locale-aware, per the number-formatting rule — and it keeps the count in
     step with the rest of the page's typography rather than raw concatenation. */
  var nf = null;
  try { nf = new Intl.NumberFormat(); } catch (e) { nf = null; }
  function fmt(n) { return nf ? nf.format(n) : String(n); }

  /* The search term belongs in the URL. This page exists to be linked at — a
     support reply saying "search for eartips" is worse than a link that arrives
     already filtered — and without it a reload throws the query away. */
  function syncUrl(term) {
    if (!window.history || !window.history.replaceState) return;
    try {
      var url = new URL(window.location.href);
      if (term) url.searchParams.set('q', term);
      else url.searchParams.delete('q');
      window.history.replaceState({}, '', url);
    } catch (e) { /* older browser: filtering still works, the URL just does not follow */ }
  }

  var typing;
  search.addEventListener('input', function () {
    clearTimeout(typing);
    typing = setTimeout(function () { apply(); syncUrl(search.value.trim()); }, 90);
    if (clearBtn) clearBtn.classList.toggle('is-on', search.value !== '');
  });

  if (clearBtn) {
    clearBtn.addEventListener('click', function () {
      search.value = '';
      apply();
      syncUrl('');
      search.focus();
    });
  }

  document.addEventListener('keydown', function (e) {
    if (e.key === '/' && document.activeElement !== search) {
      var tag = (document.activeElement.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
      e.preventDefault();
      search.focus();
      search.select();
    } else if (e.key === 'Escape' && document.activeElement === search && search.value !== '') {
      search.value = '';
      apply();
      syncUrl('');
    }
  });

  /* ---------- arriving on an anchor ---------- */
  /* A footer link lands on #warranty; open that group's first answer so the
     reader sees content, not a row of collapsed titles. */
  function openFromHash() {
    var id = (window.location.hash || '').replace('#', '');
    if (!id) return;
    var target = document.getElementById(id);
    if (!target) return;

    /* #how-do-i-start-a-return — one specific answer. Every entry carries its
       question handleized as an id, so support can link straight at the sentence
       that answers the email they are replying to. */
    if (target.classList.contains('sp-item')) {
      items.forEach(function (other) { if (other !== target) setOpen(other, false); });
      setOpen(target, true);
      var owner = target.getAttribute('data-in-group');
      navLinks.forEach(function (l) {
        l.classList.toggle('is-on', l.getAttribute('data-nav-for') === owner);
      });
      return;
    }

    /* #warranty — a whole group; open its first answer so the reader lands on
       content rather than a row of collapsed titles. */
    if (!target.classList.contains('sp-group')) return;
    var first = target.querySelector('.sp-item');
    if (first) setOpen(first, true);
    navLinks.forEach(function (l) {
      l.classList.toggle('is-on', l.getAttribute('data-nav-for') === id);
    });
  }
  window.addEventListener('hashchange', openFromHash);

  /* ---------- the rail follows the reader ---------- */
  function armNav() {
    if (!g || !window.ScrollTrigger || still || !navLinks.length) return;
    var mm = g.matchMedia();
    mm.add('(min-width: 1024px) and (prefers-reduced-motion: no-preference)', function () {
      var triggers = groups.map(function (group) {
        return window.ScrollTrigger.create({
          trigger: group,
          start: 'top 30%',
          end: 'bottom 30%',
          onToggle: function (self) {
            if (!self.isActive) return;
            var id = group.getAttribute('data-group');
            navLinks.forEach(function (l) {
              l.classList.toggle('is-on', l.getAttribute('data-nav-for') === id);
            });
          }
        });
      });
      /* onToggle only fires on a CHANGE, so the group already under the line at
         load never reports itself — same trap as the header theme switch. */
      var first = triggers.filter(function (t) { return t.isActive; })[0] || triggers[0];
      if (first) {
        var id = first.trigger.getAttribute('data-group');
        navLinks.forEach(function (l) {
          l.classList.toggle('is-on', l.getAttribute('data-nav-for') === id);
        });
      }
      return function () { triggers.forEach(function (t) { t.kill(); }); };
    });
  }

  /* arriving with ?q=… — a shared link, or a reload */
  (function fromUrl() {
    try {
      var q = new URL(window.location.href).searchParams.get('q');
      if (q) {
        search.value = q;
        if (clearBtn) clearBtn.classList.add('is-on');
      }
    } catch (e) { /* no URL API: start empty */ }
  })();

  /* ---------- the hero's remote control ---------- */
  /* sections/sp-hero renders a search ABOVE this hub and shows its hits in a
     panel under the field, because the hub itself starts ~900px down the page:
     typing and seeing nothing move is what a broken search feels like
     (Rene 2026-08-28). It reads the SAME items and the SAME matcher through
     this handle rather than carrying a second index — two indexes over one
     page is how a search starts disagreeing with itself. */
  window.KiwiHelp = {
    items: items,
    matches: matches,
    apply: apply,
    /* label() is here rather than in the hero because the markup it reads —
       the button's first span, the group's data-group-label — belongs to this
       section; the hero should not have to know its shape. */
    label: function (item) {
      var q = item.querySelector('button span');
      var grp = item.closest('.sp-group');
      return {
        q: q ? q.textContent.trim() : '',
        group: grp ? (grp.getAttribute('data-group-label') || '') : '',
        id: item.id
      };
    },
    open: function (item) {
      items.forEach(function (other) { if (other !== item) setOpen(other, false); });
      setOpen(item, true);
    }
  };

  apply();
  openFromHash();
  if (document.readyState === 'complete') armNav();
  else window.addEventListener('load', armNav);
})();
